import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { motion } from 'framer-motion';

const CardVisualsSection = ({ currentLanguage }) => {
  const [activeView, setActiveView] = useState('front');
  const [isRotating, setIsRotating] = useState(false);

  const content = {
    EN: {
      title: "Meet Your PSPXpress Card",
      subtitle: "Premium design meets practical functionality",
      views: {
        front: "Front",
        back: "Back", 
        profile: "Profile"
      },
      features: [
        "Reloadable prepaid card",
        "No credit check required",
        "Accepted worldwide",
        "Secure chip technology"
      ]
    },
    FR: {
      title: "Rencontrez votre carte PSPXpress", 
      subtitle: "Le design premium rencontre la fonctionnalité pratique",
      views: {
        front: "Devant",
        back: "Arrière",
        profile: "Profil"
      },
      features: [
        "Carte prépayée rechargeable",
        "Aucune vérification de crédit requise",
        "Accepté dans le monde entier",
        "Technologie de puce sécurisée"
      ]
    }
  };

  const text = content?.[currentLanguage] || content?.EN;

  // Auto-rotate through views
  useEffect(() => {
    const interval = setInterval(() => {
      const views = ['front', 'back', 'profile'];
      const currentIndex = views?.indexOf(activeView);
      const nextIndex = (currentIndex + 1) % views?.length;
      setActiveView(views?.[nextIndex]);
      setIsRotating(true);
      setTimeout(() => setIsRotating(false), 800);
    }, 4000);

    return () => clearInterval(interval);
  }, [activeView]);

  const handleViewChange = (view) => {
    setActiveView(view);
    setIsRotating(true);
    setTimeout(() => setIsRotating(false), 800);
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {text?.title}
          </h2>
          <p className="text-xl text-gray-600">
            {text?.subtitle}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          {/* 3D Card Display */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative flex justify-center"
          >
            <div className="relative w-96 h-64">
              {/* Card Container with 3D effect */}
              <motion.div
                className="relative w-full h-full preserve-3d"
                animate={{ 
                  rotateY: activeView === 'back' ? 180 : 0,
                  rotateX: activeView === 'profile' ? -15 : 0,
                  scale: isRotating ? 1.05 : 1
                }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
                style={{ 
                  transformStyle: 'preserve-3d',
                  perspective: '1000px'
                }}
              >
                {/* Front of Card */}
                <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 rounded-2xl shadow-2xl backface-hidden">
                  <div className="p-8 h-full flex flex-col justify-between text-white relative overflow-hidden">
                    {/* Background pattern */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-4 right-4 w-32 h-32 border-2 border-white rounded-full"></div>
                      <div className="absolute bottom-8 left-8 w-24 h-24 border border-white rounded-full"></div>
                    </div>
                    
                    <div className="relative z-10">
                      <div className="flex justify-between items-start mb-8">
                        <div>
                          <div className="text-lg font-bold">PSPXpress</div>
                          <div className="text-sm opacity-75">Prepaid Card</div>
                        </div>
                        <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                          <Icon name="CreditCard" size={28} className="text-white" />
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="font-mono text-2xl tracking-widest">
                          •••• •••• •••• 1234
                        </div>
                        <div className="flex justify-between items-end">
                          <div>
                            <div className="text-xs opacity-75 uppercase">Valid Thru</div>
                            <div className="text-lg font-semibold">12/27</div>
                          </div>
                          <div className="text-right">
                            {/* Updated Mastercard logo with provided asset */}
                            <div className="w-16 h-10 mb-1 overflow-hidden rounded">
                              <Image
                                src="/assets/images/image-1757454378302.png"
                                alt="Mastercard"
                                className="w-full h-full object-contain"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Back of Card */}
                <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-blue-800 to-blue-900 rounded-2xl shadow-2xl backface-hidden" style={{ transform: 'rotateY(180deg)' }}>
                  <div className="p-8 h-full flex flex-col justify-center text-white">
                    {/* Magnetic stripe */}
                    <div className="w-full h-12 bg-gray-800 mb-6 rounded"></div>
                    
                    {/* Signature strip */}
                    <div className="bg-white h-8 mb-4 rounded flex items-center px-4">
                      <div className="text-gray-800 text-sm">Authorized Signature</div>
                    </div>
                    
                    {/* Security features */}
                    <div className="text-center space-y-2">
                      <div className="text-sm opacity-75">Security Features</div>
                      <div className="flex justify-center space-x-4">
                        <Icon name="Shield" size={20} />
                        <Icon name="Lock" size={20} />
                        <Icon name="Eye" size={20} />
                      </div>
                    </div>
                    
                    {/* Customer service */}
                    <div className="text-center mt-6">
                      <div className="text-xs opacity-75">Customer Service</div>
                      <div className="text-sm font-semibold">1-800-PSP-CARD</div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Shadow */}
              <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-80 h-4 bg-black/20 rounded-full blur-lg"></div>
            </div>

            {/* View Controls */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2">
              {['front', 'back', 'profile']?.map((view) => (
                <button
                  key={view}
                  onClick={() => handleViewChange(view)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeView === view
                      ? 'bg-primary text-white' :'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  {text?.views?.[view]}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Features List */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {text?.features?.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center space-x-4"
              >
                <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="Check" size={24} className="text-success" />
                </div>
                <div>
                  <div className="text-lg font-semibold text-gray-900">{feature}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CardVisualsSection;